import React, { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { AnimatedNumber } from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { HeightReveal } from '../../../../components/motion/index';
import { getShareDeltaSign } from '../../../../utils/format-share-delta.util';

/** Same pace as the other live-calculated figures in forms (reverse vesting). */
const COUNT_DURATION_MS = 250;

// `AnimatedNumber` defaults to tabular digits; these figures sit under the shares input, which
// draws the same number in Inter's proportional digits, so they would read as another font.
const countClassName = 'proportional-nums';

const colorTransitionClassName = 'transition-colors duration-200 motion-reduce:transition-none';

const getDeltaClassName = (delta: number) =>
  delta > 0 ? 'text-forest-600' : delta < 0 ? 'text-fireside-600' : 'text-gray-700';

export type PoolSizeRowProps = {
  /** Change against the current size; shown green when it grows the pool, red when it shrinks it. */
  delta?: number;
  label: ReactNode;
  sharesCount: number;
};

// The figures count to each new value as the amount is typed instead of jumping per keystroke.
export const PoolSizeRow: FC<PoolSizeRowProps> = ({ label, sharesCount, delta = 0 }) => (
  <div className="flex w-full items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
    <div className="flex min-h-6 flex-1 flex-col justify-center">
      <span className="text-sm font-[450] text-gray-700">{label}</span>
      <HeightReveal isShown={delta !== 0}>
        <span
          className={twMerge(
            'block !text-label-md font-medium',
            colorTransitionClassName,
            getDeltaClassName(delta),
          )}
        >
          <AppFormattedMessage
            id="POOL_SIZE_DELTA"
            values={{
              delta: (
                <>
                  {getShareDeltaSign(delta, { spaced: true })}{' '}
                  <AnimatedNumber
                    className={countClassName}
                    duration={COUNT_DURATION_MS}
                    integer
                    value={Math.abs(delta)}
                  />
                </>
              ),
              count: Math.abs(delta),
            }}
          />
        </span>
      </HeightReveal>
    </div>
    <span
      className={twMerge(
        'shrink-0 text-sm',
        colorTransitionClassName,
        delta === 0 ? 'font-[450]' : 'font-[550]',
        getDeltaClassName(delta),
      )}
    >
      <AppFormattedMessage
        id="POOL_SIZE_SHARES"
        values={{
          shares: (
            <AnimatedNumber
              className={countClassName}
              duration={COUNT_DURATION_MS}
              integer
              value={sharesCount}
            />
          ),
          count: sharesCount,
        }}
      />
    </span>
  </div>
);
